import { useEffect, useState } from "react";
import { File as FileIcon, TriangleAlert } from "lucide-react";

import { kaiApi } from "@/services/kaiApi";
import type { MessageAttachmentRef } from "@/lib/kaiAttachments";

interface KaiMessageAttachmentsProps {
  conversationId: string;
  attachments: MessageAttachmentRef[];
}

interface ThumbnailState {
  url?: string;
  loading: boolean;
  failed: boolean;
}

/**
 * Renders files/images attached to a sent user message inside the chat flow.
 * Images show inline thumbnails fetched from the server; other files render as
 * compact chips. Order matches the upload order stored in message metadata.
 */
export function KaiMessageAttachments({
  conversationId,
  attachments,
}: KaiMessageAttachmentsProps) {
  const [thumbnails, setThumbnails] = useState<Record<string, ThumbnailState>>(
    {},
  );

  useEffect(() => {
    const imageAttachments = attachments.filter((att) => att.isImage);
    if (imageAttachments.length === 0) return;

    let cancelled = false;
    const urlsToRevoke: string[] = [];

    setThumbnails((prev) => {
      const next = { ...prev };
      for (const att of imageAttachments) {
        next[att.id] = { loading: true, failed: false };
      }
      return next;
    });

    void (async () => {
      await Promise.all(
        imageAttachments.map(async (att) => {
          try {
            const url = await kaiApi.getAttachmentRawUrl(conversationId, att.id);
            if (cancelled) {
              URL.revokeObjectURL(url);
              return;
            }
            urlsToRevoke.push(url);
            setThumbnails((prev) => ({
              ...prev,
              [att.id]: { url, loading: false, failed: false },
            }));
          } catch {
            if (!cancelled) {
              setThumbnails((prev) => ({
                ...prev,
                [att.id]: { loading: false, failed: true },
              }));
            }
          }
        }),
      );
    })();

    return () => {
      cancelled = true;
      for (const url of urlsToRevoke) {
        URL.revokeObjectURL(url);
      }
    };
  }, [attachments, conversationId]);

  if (attachments.length === 0) return null;

  return (
    <div className="mb-2 flex flex-wrap justify-end gap-1.5">
      {attachments.map((att) => {
        if (att.isImage) {
          const thumb = thumbnails[att.id];
          if (thumb?.url) {
            return (
              <img
                key={att.id}
                src={thumb.url}
                alt={att.filename}
                title={att.filename}
                className="size-16 rounded-lg border border-border/50 object-cover"
              />
            );
          }
          if (thumb?.failed) {
            return (
              <span
                key={att.id}
                title={att.filename}
                className="inline-flex max-w-[12rem] items-center gap-1.5 rounded-lg border border-border/50 bg-foreground/[0.04] px-2 py-1 text-xs text-muted-foreground"
              >
                <TriangleAlert className="size-3.5 shrink-0 text-amber-500" />
                <span className="truncate">{att.filename}</span>
              </span>
            );
          }
          return (
            <div
              key={att.id}
              aria-label={`Loading ${att.filename}`}
              className="size-16 animate-pulse rounded-lg border border-border/50 bg-foreground/[0.06]"
            />
          );
        }

        return (
          <span
            key={att.id}
            title={att.filename}
            className="inline-flex max-w-[12rem] items-center gap-1.5 rounded-lg border border-border/50 bg-foreground/[0.04] px-2 py-1 text-xs text-foreground"
          >
            <FileIcon className="size-3.5 shrink-0 text-muted-foreground" />
            <span className="truncate">{att.filename}</span>
          </span>
        );
      })}
    </div>
  );
}
